import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Badge } from "@/components/common/Badge";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { useProviders } from "@/lib/api/apps/hooks";
import type { ProviderCatalog } from "@/lib/api/apps/types";
import { formatDayMonth } from "@/lib/dates";
import { AppPageHead, AppPageShell } from "./page-shell";

type KindId = "design" | "research" | "capture";

interface Kind {
  id: KindId;
  title: string;
  fact: string;
  tags: string[];
  writes: string;
}

/** Ordered as a table's life: shape it, fill it, collect into it. */
const KINDS: Kind[] = [
  {
    id: "design",
    title: "Design",
    fact: "Drafts new tables with you, round by round.",
    tags: ["Agent", "Interactive"],
    writes: "a model you pick",
  },
  {
    id: "research",
    title: "Research",
    fact: "Fills tables you lend it from the web.",
    tags: ["Agent", "Scheduled"],
    writes: "tables you lend it",
  },
  {
    id: "capture",
    title: "Capture",
    fact: "Collects readings and scans from phones in the field.",
    tags: ["Mobile", "Offline"],
    writes: "tables it sets up",
  },
];

/** Where a call to action leads: a section of the org's settings. */
type Target = "models" | "plan";

export type Availability =
  | { open: true; trial: boolean }
  | {
      open: false;
      badge?: string;
      hint?: string;
      action?: { label: string; to: Target };
    };

type Closed = Extract<Availability, { open: false }>;

/** A call to action for who may act, else who to ask. */
const act = (
  may: boolean,
  badge: string,
  hint: string,
  action: Closed["action"] & object,
  ask: string,
): Closed =>
  may
    ? { open: false, badge, hint, action }
    : { open: false, badge, hint: `${hint} ${ask}` };

const ADD_KEY = { label: "Add a model key", to: "models" } as const;
const ADD_OWN_KEY = { label: "Add your own model key", to: "models" } as const;
const UPGRADE = { label: "Upgrade to Pro", to: "plan" } as const;
const COMING_SOON: Availability = { open: false, badge: "Coming soon" };

export function availability(
  kind: KindId,
  catalog: ProviderCatalog | undefined,
): Availability {
  if (kind === "capture") return COMING_SOON;
  if (!catalog) return { open: false };
  const ownKey = catalog.providers.some((p) => p.key === "on_file");
  const house = catalog.providers.find((p) => !p.needs_key);
  if (!ownKey) {
    if (!house)
      return act(
        catalog.can_manage_keys,
        "Needs a key",
        "Apps here run on the organization’s own model key.",
        ADD_KEY,
        "Ask an owner or admin to add one.",
      );
    if (house.key === "spent")
      return {
        open: false,
        badge: "Trial used",
        hint: "Your trial’s AI allowance is used up.",
        action: ADD_OWN_KEY,
      };
    if (house.key !== "house")
      return {
        open: false,
        badge: "Needs a key",
        hint: "The trial is not available right now.",
        action: ADD_OWN_KEY,
      };
  }
  const trial = !ownKey;
  const paid = catalog.paid_searches;
  if (kind === "design" || !paid?.stop) return { open: true, trial };

  const personal = Boolean(house);
  const used = `All ${catalog.searches.limit} trial web lookups are used.`;
  switch (paid.stop) {
    case "needs_pro":
      if (personal)
        return paid.sold
          ? {
              open: false,
              badge: "Web lookups used",
              hint: used,
              action: UPGRADE,
            }
          : {
              open: false,
              badge: "Web lookups used",
              hint: `${used} More web lookups are coming soon.`,
            };
      return act(
        paid.can_manage,
        "Needs Pro",
        "Research apps come with a Pro plan.",
        UPGRADE,
        "Ask an owner to upgrade.",
      );
    case "coming_soon":
      return {
        open: false,
        badge: personal ? "Web lookups used" : "Web lookups off",
        hint: `${personal ? `${used} ` : ""}Paid web lookups are coming soon.`,
      };
    case "needs_key":
      return {
        open: false,
        badge: "Web lookups used",
        hint: `${used} Paid web lookups run with your own model key.`,
        action: ADD_OWN_KEY,
      };
    case "off":
      return act(
        paid.can_manage,
        "Web lookups off",
        personal
          ? `${used} Turn on paid web lookups to keep researching.`
          : "Research needs paid web lookups turned on.",
        { label: "Turn on paid web lookups", to: "models" },
        "Ask an owner to turn them on.",
      );
    case "price_changed":
      return act(
        paid.can_manage,
        "Price changed",
        "The paid web lookup price went up; web lookups wait until an owner agrees to it.",
        { label: "Review the new price", to: "models" },
        "Ask an owner to agree to it.",
      );
    case "limit_reached":
      return act(
        paid.can_manage,
        "Web lookup limit",
        `This month’s ${paid.limit ?? 0} paid web lookups are used; the limit resets on ${formatDayMonth(paid.resets_at * 1000)}.`,
        { label: "Raise the limit", to: "models" },
        "Ask an owner to raise it.",
      );
  }
}

/** `/apps/new`, and `/apps` while the org has none: what the app should do.
 *  Each kind is its own app. */
export const NewAppPage: React.FC<{ first?: boolean }> = ({
  first = false,
}) => {
  const navigate = useNavigate();
  const orgId = useOrgContextStore((s) => s.activeOrgId);
  const org = useOrgContextStore((s) =>
    s.orgs.find((o) => o.org_id === s.activeOrgId),
  );
  const catalog = useProviders(orgId).data;
  const refreshOrgs = useOrgContextStore((s) => s.refreshOrgs);
  // Which kinds an org has rides on the org list, loaded once per page load.
  useEffect(() => {
    void refreshOrgs();
  }, [refreshOrgs]);
  const usable = (k: KindId) => org?.features?.includes(k) ?? false;
  const kinds = KINDS.filter(
    (k) => usable(k.id) || org?.coming_soon?.includes(k.id),
  );
  return (
    <AppPageShell maxWidth="52rem">
      <AppPageHead
        trail={first ? [] : [{ label: "Applications", to: "/apps" }]}
        title={
          first ? "What should your first app do?" : "What should this app do?"
        }
      />
      <div
        className="grid gap-4"
        style={{
          gridTemplateColumns: "repeat(auto-fill, minmax(16rem, 1fr))",
        }}
      >
        {kinds.map((k) => {
          const a = usable(k.id) ? availability(k.id, catalog) : COMING_SOON;
          return (
            <div
              key={k.id}
              className="row-span-2 grid"
              style={{
                gridTemplateRows: "subgrid",
                rowGap: "var(--spacing-sm)",
              }}
            >
              <button
                type="button"
                disabled={!a.open}
                onClick={() => navigate(`/apps/new/${k.id}`)}
                className="group flex flex-col gap-2 rounded-app border border-border text-left enabled:hover:bg-bg-hover cursor-pointer disabled:cursor-not-allowed disabled:opacity-55"
                style={{ padding: "var(--spacing-lg) var(--spacing-xl)" }}
              >
                <span className="flex items-center gap-2 text-fg font-semibold">
                  <span style={{ fontSize: "var(--font-size-large)" }}>
                    {k.title}
                  </span>
                  {a.open && a.trial && <Badge label="Trial" variant="info" />}
                  {a.open ? (
                    <ArrowForwardIcon
                      sx={{ fontSize: 18 }}
                      className="ml-auto text-fg-muted group-hover:text-accent"
                    />
                  ) : (
                    a.badge && (
                      <span className="ml-auto">
                        <Badge label={a.badge} size="xs" />
                      </span>
                    )
                  )}
                </span>
                <span className="text-fg-muted">{k.fact}</span>
                <span className="flex gap-1">
                  {k.tags.map((t) => (
                    <Badge key={t} label={t} size="xs" />
                  ))}
                </span>
                <span className="text-mono-small-muted">
                  Writes to <span className="text-fg">{k.writes}</span>
                </span>
              </button>
              {!a.open && a.hint && (
                <span className="text-fg-muted-regular-small">
                  {a.hint}
                  {a.action && (
                    <>
                      {" "}
                      <Link
                        to={`/organizations/${orgId}#${a.action.to}`}
                        className="text-accent hover:underline"
                      >
                        {a.action.label}
                      </Link>
                    </>
                  )}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </AppPageShell>
  );
};
